import './amendments.css';
import './history.css';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { queryString } from '../../lib/api-client.js';
import { date, money, number } from '../../format.js';
import { useApiQuery, useUrlFilters } from '../../hooks.js';
import { DataNote, DataTable, FilterPanel, Loading, MetricCard, Pagination, RequestError, SelectField, SourceLink, TextField } from '../../components/ui.js';
import { PartyLogo } from '../../components/party-logo.js';
import { ProfileIcon } from '../../components/profile-icon.js';
import { ChartBarMark, ChartTooltipGroup } from '../../components/chart-tooltip.js';

type Row = Record<string, unknown>;
type ProfileForHistory = {
  id: number;
  name?: string;
  party?: string;
  role?: string;
  source?: string;
  source_url?: string | null;
  mandates?: Row[];
  affiliations?: Row[];
  coverage?: Row[];
  timeline?: Row[];
};
type AmendmentResponse = {
  items: Row[];
  total?: number;
  page?: number;
  size?: number;
  totals?: Record<'committed' | 'settled' | 'paid', number | null>;
  annual?: Array<{ year: number; committed: number | null; settled: number | null; paid: number | null }>;
  source_url?: string | null;
  chart?: Array<{ label: string; value: number }>;
  transferegov?: {
    has_records?: boolean;
    years?: number[];
    counts?: Record<string, number>;
    committed?: number | null;
    reported_payments?: number | null;
    source_urls?: Record<string, string>;
  };
  coverage?: boolean;
  note?: string;
};
type Source = { id: string; name: string; url: string; description: string; category: string | null };
type SourceResponse = Source[];

const amendmentStages = [
  { value: '', label: 'Todas as etapas' },
  { value: 'committed', label: 'Empenhado' },
  { value: 'settled', label: 'Liquidado' },
  { value: 'paid', label: 'Pago' },
];

const transferegovLabels: Record<string, string> = {
  planos_acao_especiais: 'Planos de ação',
  empenhos_especiais: 'Empenhos de transferências especiais',
  documentos_habeis_especiais: 'Documentos hábeis',
  ordens_pagamentos_ordens_bancarias_especiais: 'Ordens de pagamento e bancárias',
  relatorios_gestao_novos_especiais: 'Relatórios de gestão',
  relatorios_gestao_documento_liquidacao_especiais: 'Documentos de liquidação dos relatórios',
};

function safeValue(value: unknown, fallback = 'Não informado') {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  return String(value);
}

function formatYearRange(years: number[]) {
  const sorted = [...new Set(years)].sort((a, b) => a - b);
  if (!sorted.length) return 'Não identificada no perfil';
  return sorted.length === 1 ? String(sorted[0]) : `${sorted[0]}–${sorted[sorted.length - 1]}`;
}

function Metric({ title, value, caption, unavailable = false }: { title: string; value: unknown; caption?: string; unavailable?: boolean }) {
  return <MetricCard panel={false} className={`profile-amendment-metric${unavailable ? ' is-unavailable' : ''}`} label={title} value={unavailable ? 'Não disponível' : money(value)} detail={caption} />;
}

function StageChart({ rows }: { rows: AmendmentResponse['annual'] }) {
  const data = rows ?? [];
  const rawMax = Math.max(1, ...data.flatMap((row) => [Number(row.committed ?? 0), Number(row.settled ?? 0), Number(row.paid ?? 0)]));
  const step = 10 ** Math.floor(Math.log10(rawMax));
  const max = Math.ceil(rawMax / step) * step;
  if (!data.length) return <div className="profile-amendment-chart-empty">Não há série anual registrada para o período selecionado.</div>;
  const stages = [
    { key: 'committed', label: 'Empenhado' },
    { key: 'settled', label: 'Liquidado' },
    { key: 'paid', label: 'Pago' },
  ] as const;
  const tickCount = Math.round(max / step);
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) => max - index * step);
  const currencyTick = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return <figure className="profile-amendment-chart">
    <ChartTooltipGroup className="profile-amendment-chart-tooltip-group">
      <div className="profile-amendment-chart-plot">
        <div className="profile-amendment-chart-axis" aria-hidden="true">{ticks.map((tick) => <span key={tick}>{currencyTick.format(tick)}</span>)}</div>
        <div className="profile-amendment-chart-main">
          <div className="profile-amendment-chart-grid" aria-hidden="true">{ticks.map((tick) => <span key={tick} />)}</div>
          <div className="profile-amendment-chart-years">
            {data.map((row) => <div className="profile-amendment-chart-year" key={row.year} role="group" aria-label={`Ano ${row.year}`}>
              <div className="profile-amendment-chart-bars">
                {stages.map(({ key, label }) => {
                  const amount = Number(row[key] ?? 0);
                  const barHeight = amount > 0 ? Math.max(1, amount / max * 100) : 0;
                  return <ChartBarMark
                    className={`profile-amendment-chart-mark is-${key}`}
                    key={key}
                    shapeClassName={`profile-amendment-chart-bar is-${key}`}
                    valuePercent={barHeight}
                    datum={{ title: String(row.year), label: `${label} · CGU`, value: money(amount), color: key === 'committed' ? '#6e9fe0' : key === 'settled' ? '#4b9a70' : '#075c49' }}
                  />;
                })}
              </div>
              <span className="profile-amendment-chart-year-label">{row.year}</span>
            </div>)}
          </div>
        </div>
      </div>
      <figcaption className="profile-amendment-legend"><span className="is-committed">Empenhado · CGU</span><span className="is-settled">Liquidado · CGU</span><span className="is-paid">Pago · CGU</span></figcaption>
    </ChartTooltipGroup>
  </figure>;
}

function TransferegovPanel({ data }: { data: AmendmentResponse['transferegov'] }) {
  const transferegov = data ?? {};
  const counts = transferegov.counts ?? {};
  const countEntries = Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  return <section className="panel profile-transferegov-panel">
    <header><h2>Transferências especiais · Transferegov</h2><p>Registros associados por identificadores oficiais de plano e ano.</p></header>
    {transferegov.has_records && countEntries.length ? <>
      <div className="profile-transferegov-counts">{countEntries.map(([key, count]) => <article key={key}><strong>{number(count)}</strong><span>{transferegovLabels[key] ?? key}</span><SourceLink href={transferegov.source_urls?.[key] ?? null}>Fonte Transferegov</SourceLink></article>)}</div>
      <div className="profile-transferegov-values">
        <p><span>Empenhos vinculados</span><strong>{money(transferegov.committed)}</strong></p>
        <p><span>Pagamentos informados em relatórios</span><strong>{money(transferegov.reported_payments)}</strong></p>
      </div>
    </> : <div className="profile-transferegov-empty"><h3>Nenhum registro vinculado neste ano</h3><p>Isso indica ausência de registros associados a este perfil na consulta importada; não significa que o valor seja zero.</p></div>}
    <p className="profile-transferegov-note">Recorte do ano e do parlamentar selecionados. Os valores do Transferegov ficam separados dos totais da CGU para evitar somar registros potencialmente equivalentes. Relatórios de liquidação informam execução pelo ente beneficiário e não equivalem, por si só, a pagamento da União. Os filtros de UF e município da tabela CGU não se aplicam a este bloco.</p>
  </section>;
}

export function AmendmentsTab({ id, year, allYears }: { id: number; year: number; allYears: boolean }) {
  const { params, setValue } = useUrlFilters();
  const values = {
    year,
    all_years: allYears,
    page: params.get('page') ?? 1,
    size: 10,
    stage: params.get('stage') ?? '',
    state: params.get('state') ?? '',
    municipality: params.get('municipality') ?? '',
  };
  const query = queryString(values);
  const result = useApiQuery<AmendmentResponse>(['profile-amendments', id, query], `/api/politicians/${id}/amendments?${query}`);
  if (result.isLoading) return <div className="profile-content"><Loading /></div>;
  if (result.error) return <RequestError message={result.error.message} />;
  const data = result.data;
  if (!data) return null;
  const filterSet = (value: string) => setValue('stage', value);
  const stageLabels: Record<string, string> = { committed: 'Empenhado', settled: 'Liquidado', paid: 'Pago' };
  return <div className="profile-content profile-amendments-page">
    <header className="profile-activity-heading amendments-heading">
      <span className="profile-activity-heading-icon"><ProfileIcon name="database" size={25} /></span>
      <div><h2>Emendas parlamentares</h2><p>Registros de transferências e execução orçamentária associados ao parlamentar.</p></div>
    </header>
    <DataNote>Indicação de recurso, autorização orçamentária, empenho, liquidação e pagamento são etapas diferentes. A transferência financeira, sozinha, não comprova a execução ou entrega do objeto.</DataNote>
    <FilterPanel className="profile-amendment-filters" ariaLabel="Filtros de emendas">
      <SelectField label="Etapa financeira" value={values.stage} onValueChange={filterSet}>{amendmentStages.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</SelectField>
      <TextField label="UF de destino" value={values.state} onValueChange={(value) => setValue('state', value.toUpperCase())} placeholder="Ex.: SP" maxLength={2} />
      <TextField label="Município de destino" value={values.municipality} onValueChange={(value) => setValue('municipality', value)} placeholder="Digite o município" />
    </FilterPanel>
    <section className="profile-amendment-metrics" aria-label="Resumo financeiro das emendas">
      <Metric title="Valor indicado" value={null} unavailable caption="Consulta individual do SIOP ainda não configurada." />
      <Metric title="Valor autorizado" value={null} unavailable caption="Consulta individual do SIOP ainda não configurada." />
      {(['committed', 'settled', 'paid'] as const).map((key) => <Metric key={key} title={`Valor ${stageLabels[key]!.toLocaleLowerCase('pt-BR')}`} value={data.totals?.[key]} caption={key === 'committed' ? 'CGU · comprometido no orçamento.' : key === 'settled' ? 'CGU · despesa liquidada.' : 'CGU · pagamento registrado.'} />)}
    </section>
    <TransferegovPanel data={data.transferegov} />
    <section className="panel profile-amendment-annual"><h2>Evolução anual dos valores registrados</h2><p>Séries CGU de empenho, liquidação e pagamento, sem estimar indicação ou autorização.</p><StageChart rows={data.annual} /></section>
    <section className="profile-amendment-details">
      <header><div><h2>Emendas destinadas · CGU</h2><p>Registros individuais conforme os campos realmente disponíveis.</p></div></header>
      <div className="profile-amendment-table">
        {!data.items.length ? <div className="profile-amendment-no-records">Nenhum registro CGU corresponde aos filtros atuais. A ausência não confirma valor zero.</div> : <DataTable><thead><tr><th>Ano</th><th>Destino</th><th>Favorecido</th><th>Empenhado</th><th>Liquidado</th><th>Pago</th><th>Etapa financeira observada</th><th>Fonte oficial</th></tr></thead><tbody>{data.items.map((item, index) => {
        const paid = item.paid;
        const settled = item.settled;
        const committed = item.committed;
        const observed = Number(paid ?? 0) > 0 ? 'Pagamento registrado' : Number(settled ?? 0) > 0 ? 'Despesa liquidada' : Number(committed ?? 0) > 0 ? 'Empenho registrado' : 'Registro financeiro disponível';
        return <tr key={String(item.external_id ?? index)}><td>{safeValue(item.year)}</td><td>{[item.municipality, item.state].filter(Boolean).map(String).join(' · ') || 'MÚLTIPLO'}</td><td>{safeValue(item.beneficiary, 'Não identificado pela consulta agregada')}</td><td>{money(committed)}</td><td>{money(settled)}</td><td>{money(paid)}</td><td>{observed}</td><td><SourceLink href={typeof item.source_url === 'string' ? item.source_url : data.source_url ?? null}>CGU</SourceLink></td></tr>;
      })}</tbody></DataTable>}
      </div>
      <Pagination page={Number(data.page ?? 1)} size={Number(data.size ?? 10)} total={Number(data.total ?? 0)} onChange={(page) => setValue('page', String(page), { resetPage: false })} />
    </section>
  </div>;
}

const coverageDatasets = [
  { key: 'despesas', label: 'Despesas' },
  { key: 'emendas', label: 'Emendas' },
  { key: 'proposicoes', label: 'Proposições' },
  { key: 'votacoes', label: 'Votações nominais' },
];

function CoverageCell({ item, highlight }: { item: Row | undefined; highlight: boolean }) {
  const count = item ? Number(item.count ?? 0) : null;
  const state = !item ? 'no-coverage' : count! > 0 ? 'has-records' : 'collected-empty';
  const label = !item ? 'Sem cobertura registrada' : count! > 0 ? `${number(count)} registro(s)` : 'Coletado sem registros';
  return <td className={`profile-coverage-cell is-${state}${highlight ? ' is-highlighted' : ''}`}><span className="profile-coverage-dot" aria-hidden="true" /><span className="profile-coverage-label">{label}</span></td>;
}

function PoliticalHistory({ profile }: { profile: ProfileForHistory }) {
  const mandates = [...(profile.mandates ?? [])].sort((a, b) => String(b.start ?? '').localeCompare(String(a.start ?? '')));
  const affiliations = [...(profile.affiliations ?? [])].sort((a, b) => String(b.start ?? '').localeCompare(String(a.start ?? '')));
  return <section className="panel profile-history-political">
    <h2>Histórico político</h2>
    <p>Mandatos e filiações ao longo do tempo, conforme os registros disponíveis.</p>
    <h3>Mandatos e exercício</h3>
    {mandates.length ? <div className="profile-history-mandates">{mandates.map((item, index) => {
      const start = item.start ? String(item.start).slice(0, 4) : 'Período não informado';
      const end = item.end ? String(item.end).slice(0, 4) : 'atual';
      return <article key={String(item.id ?? `${item.start}-${index}`)}><span className={`profile-history-dot${item.effective ? ' is-active' : ''}`} aria-hidden="true" /><strong>{start} – {end}</strong><span><b>{safeValue(item.role, profile.role ?? 'Mandato')}</b><em className={item.effective ? 'is-effective' : 'is-unconfirmed'}>{item.effective ? 'Em exercício' : 'Situação não confirmada'}</em><small>{[item.state, item.legislature ? `${String(item.legislature)}ª Legislatura` : null].filter(Boolean).join(' · ')}</small></span></article>;
    })}</div> : <p className="empty-inline">Nenhum mandato registrado.</p>}
    <h3>Filiações partidárias</h3>
    {affiliations.length ? <div className="profile-history-affiliations">{affiliations.map((item, index) => {
      const party = safeValue(item.party, 'Partido não informado');
      const start = item.start ? String(item.start).slice(0, 4) : '—';
      const end = item.end ? String(item.end).slice(0, 4) : 'atual';
      const years = `${start} – ${end}`;
      return <article key={`${party}-${item.start ?? index}-${index}`}><strong>{years}</strong><PartyLogo abbreviation={party} size="affiliation" decorative /><b>{party}{item.party_name ? ` · ${String(item.party_name)}` : ''}</b><SourceLink href={typeof item.source_url === 'string' ? item.source_url : null}>Fonte TSE</SourceLink></article>;
    })}</div> : <p className="empty-inline">Nenhuma filiação partidária registrada.</p>}
  </section>;
}

function CoverageMatrix({ coverage, year }: { coverage: Row[]; year: number }) {
  const { params, setValue } = useUrlFilters();
  const rows = useMemo(() => {
    const grouped = new Map<number, Map<string, Row>>();
    for (const item of coverage) {
      const y = Number(item.year);
      if (!Number.isFinite(y)) continue;
      const row = grouped.get(y) ?? new Map<string, Row>();
      row.set(String(item.dataset ?? ''), item);
      grouped.set(y, row);
    }
    return [...grouped.entries()].sort(([a], [b]) => b - a).map(([yearValue, records]) => ({ year: yearValue, records }));
  }, [coverage]);
  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const requestedPage = Number(params.get('coverage_page') ?? 1);
  const page = Math.min(pageCount, Math.max(1, Number.isFinite(requestedPage) ? Math.trunc(requestedPage) : 1));
  const visibleRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const setPage = (nextPage: number) => setValue('coverage_page', nextPage <= 1 ? '' : String(nextPage), { resetPage: false });
  return <section className="panel profile-coverage-panel"><h2>Cobertura de dados por ano e base</h2><p>Disponibilidade por período nos conjuntos coletados para este perfil.</p>
    {rows.length ? <DataTable tableClassName="profile-coverage-table"><thead><tr><th>Ano</th>{coverageDatasets.map((dataset) => <th key={dataset.key}>{dataset.label}</th>)}</tr></thead><tbody>{visibleRows.map(({ year: rowYear, records }) => <tr key={rowYear}><th scope="row">{rowYear}</th>{coverageDatasets.map((dataset) => <CoverageCell key={dataset.key} item={records.get(dataset.key)} highlight={rowYear === year} />)}</tr>)}</tbody></DataTable> : <p className="empty-inline">A cobertura ainda não foi registrada para este perfil.</p>}
    <Pagination page={page} size={pageSize} total={rows.length} onChange={setPage} showJump={false} pageLabel={`Página ${page} de ${pageCount} · ${number(rows.length)} registros`} ariaLabel="Paginação da cobertura por ano" className="profile-coverage-pagination" />
    <div className="profile-coverage-legend"><span className="is-has-records">Com registros</span><span className="is-collected-empty">Coletado sem registros</span><span className="is-no-coverage">Sem cobertura registrada</span></div>
  </section>;
}

function datasetsForSource(source: Source, profileSource: string | undefined) {
  if (source.id === 'cgu') return ['emendas'];
  if (profileSource === 'camara') {
    if (source.id === 'camara') return ['despesas', 'proposicoes', 'votacoes'];
    if (source.id === 'camara_votos') return ['votacoes'];
  }
  if (profileSource === 'senado') {
    if (source.id === 'senado') return ['proposicoes', 'votacoes'];
    if (source.id === 'senado_despesas') return ['despesas'];
  }
  return [];
}

function sourceMatch(source: Source, row: Row) {
  const sourceUrl = typeof row.source_url === 'string' ? row.source_url : '';
  if (!sourceUrl) return false;
  try { return new URL(sourceUrl).host === new URL(source.url).host; } catch { return false; }
}

function SourceCatalog({ profile, coverage }: { profile: ProfileForHistory; coverage: Row[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const result = useApiQuery<SourceResponse>(['profile-source-catalog'], '/api/sources');
  const page = Math.max(1, Number(params.get('sources_page') ?? 1));
  const size = 10;
  const sources = result.data ?? [];
  const pages = Math.max(1, Math.ceil(sources.length / size));
  const currentPage = Math.min(page, pages);
  const visible = sources.slice((currentPage - 1) * size, currentPage * size);
  const sourceCoverage = (source: Source) => {
    const timeline = (profile.timeline ?? []).filter((row) => sourceMatch(source, row));
    const datasets = [...new Set([...timeline.map((row) => String(row.dataset ?? '').split(':')[0]), ...datasetsForSource(source, profile.source)])].filter(Boolean);
    const years = coverage.filter((row) => datasets.includes(String(row.dataset ?? ''))).map((row) => Number(row.year)).filter(Number.isFinite);
    const lastCollected = timeline.map((row) => String(row.captured_at ?? '')).filter(Boolean).sort((a, b) => b.localeCompare(a))[0];
    return { lastCollected, years: [...new Set(years)] };
  };
  if (result.isLoading) return <section className="panel profile-source-catalog"><h2>Fontes oficiais e metodologia</h2><Loading /></section>;
  if (result.error) return <section className="panel profile-source-catalog"><h2>Fontes oficiais e metodologia</h2><RequestError message={result.error.message} /></section>;
  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(location.search);
    if (nextPage <= 1) next.delete('sources_page'); else next.set('sources_page', String(nextPage));
    navigate(`${location.pathname}?${next.toString()}`);
  };
  return <section className="panel profile-source-catalog"><h2>Fontes oficiais e metodologia</h2><p>Fontes cadastradas no sistema. A coleta e o período disponível variam conforme o conjunto de dados.</p>
    {visible.length ? <div className="profile-source-grid">{visible.map((source) => {
      const meta = sourceCoverage(source);
      return <article className="profile-source-card" key={source.id}><h3>{source.name}</h3><p>{source.description}</p><div className="profile-source-meta"><span>Última coleta: {meta.lastCollected ? date(meta.lastCollected) : 'Sem coleta registrada'}</span><span>Cobertura: {formatYearRange(meta.years)}</span></div><SourceLink href={source.url}>Consultar no portal oficial</SourceLink></article>;
    })}</div> : <p className="empty-inline">Não há fontes cadastradas.</p>}
    <Pagination page={currentPage} size={size} total={sources.length} onChange={setPage} />
  </section>;
}

function SnapshotHistory({ profile }: { profile: ProfileForHistory }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const timeline = profile.timeline ?? [];
  const size = 10;
  const pages = Math.max(1, Math.ceil(timeline.length / size));
  const page = Math.min(pages, Math.max(1, Number(params.get('timeline_page') ?? 1)));
  const visible = timeline.slice((page - 1) * size, page * size);
  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(location.search);
    if (nextPage <= 1) next.delete('timeline_page'); else next.set('timeline_page', String(nextPage));
    navigate(`${location.pathname}?${next.toString()}`);
  };
  return <section className="panel profile-history-snapshots">
    <h2>Versões preservadas dos dados</h2>
    <p>Data de captura do registro original recebido.</p>
    {visible.length ? <div className="profile-snapshot-list">{visible.map((item, index) => <article key={String(item.id ?? `${item.dataset}-${item.captured_at}-${index}`)}><strong>{safeValue(item.dataset, 'Conjunto não informado')} · {item.captured_at ? date(String(item.captured_at)) : 'Data não informada'}</strong><SourceLink href={typeof item.source_url === 'string' ? item.source_url : null} /></article>)}</div> : <p className="empty-inline">Nenhuma versão preservada. O histórico será iniciado após uma coleta.</p>}
    <Pagination page={page} size={size} total={timeline.length} onChange={setPage} />
  </section>;
}

export function HistoryTab({ profile, year, allYears }: { profile: ProfileForHistory; year: number; allYears: boolean }) {
  const coverage = profile.coverage ?? [];
  return <div className="profile-content profile-history-page" data-period={allYears ? 'all' : year}>
    <div className="profile-history-grid"><PoliticalHistory profile={profile} /><CoverageMatrix coverage={coverage} year={year} /></div>
    <SourceCatalog profile={profile} coverage={coverage} />
    <DataNote>Dados indisponíveis não significam zero. A ausência pode decorrer de falta de cobertura, limitações da fonte ou de um período ainda não coletado.</DataNote>
    <SnapshotHistory profile={profile} />
  </div>;
}
